import { useMemo } from 'react';
import { IconButton, IconButtonSizes, IconButtonStyles, Icons, LayoutCarousel, SeverityLevel } from '@silverfort/sf-components';
import Avatar from '../Avatar/Avatar';
import { splitArrayIntoBatches } from '../../utils';

const DEFAULT_BATCH_SIZE = 3;

export interface RiskyEntity {
    name: string;
    guid: string;
    riskLevel: number;
}

interface TopRiskEntityChartProps {
    title: string;
    list: RiskyEntity[];
    isLoading: boolean;
    showAvatar?: boolean;
    onInvestigationClick?: (guid: string) => void;
}

const EntityRow = ({
    entity,
    showAvatar,
    onInvestigationClick
}: {
    entity: RiskyEntity;
    showAvatar?: boolean;
    onInvestigationClick?: (guid: string) => void;
}) => {
    return (
        <div className="flex flex-row items-center justify-between flex-1 bg-white min-h-10 max-h-10 w-full group hover:bg-sf-gray-50 rounded-lg p-1">
            <div className="flex flex-row gap-2 flex-1 items-center">
                <Avatar name={entity.name} show={showAvatar} />
                <div className="text-13 text-sf-gray-800 group-hover:underline">{entity.name}</div>
                {onInvestigationClick && (
                    <IconButton
                        className="opacity-0 group-hover:opacity-100 text-sf-gray-400"
                        sizeVariant={IconButtonSizes.Large}
                        buttonStyle={IconButtonStyles.Secondary}
                        iconSize={16}
                        icon={Icons.Investigate}
                        onClick={() => onInvestigationClick(entity.guid)}
                    />
                )}
            </div>
            <div>
                <SeverityLevel severity={entity.riskLevel} />
            </div>
        </div>
    );
};

const LoadingRow = ({ showAvatar }: { showAvatar?: boolean }) => (
    <div className="flex flex-row items-center justify-between flex-1 bg-white h-10 w-full p-1 rounded-lg">
        <div className="flex-1 flex items-center gap-2">
            {showAvatar && <div className="w-8 h-8 rounded-full bg-sf-gray-200 animate-pulse" />}
            <div className="h-4 w-32 bg-sf-gray-200 rounded animate-pulse" />
        </div>
        <div className="flex items-center">
            <div className="h-6 w-16 bg-sf-gray-200 rounded animate-pulse" />
        </div>
    </div>
);

const NoDataRow = ({ showAvatar, isFirstRow }: { showAvatar?: boolean; isFirstRow?: boolean }) => (
    <div className="flex flex-row items-center justify-between flex-1 bg-white h-10 w-full p-1 rounded-lg">
        <div className="flex-1 flex items-center gap-2">
            {showAvatar && <div className="w-8 h-8 rounded-full bg-sf-gray-200" />}
            <div className="text-13 text-sf-gray-500">{isFirstRow ? 'No data' : ''}</div>
        </div>
        <div className="flex items-center">
            <div className="h-6 w-16 bg-sf-gray-200 rounded" />
        </div>
    </div>
);

const TopRiskEntityChart = ({
    title,
    list,
    isLoading,
    showAvatar = false,
    onInvestigationClick
}: TopRiskEntityChartProps) => {
    const batches = useMemo(() => splitArrayIntoBatches(list, DEFAULT_BATCH_SIZE), [list]);

    const pages = useMemo(() => {
        if (isLoading) {
            return [
                <div key="loading" className="flex flex-1 flex-col w-full gap-2 items-start">
                    {Array.from({ length: DEFAULT_BATCH_SIZE }, (_, index) => (
                        <LoadingRow key={index} showAvatar={showAvatar} />
                    ))}
                </div>
            ];
        }

        if (list.length === 0) {
            return [
                <div key="no-data" className="flex flex-1 flex-col w-full gap-2 items-start">
                    {Array.from({ length: DEFAULT_BATCH_SIZE }, (_, index) => (
                        <NoDataRow key={index} showAvatar={showAvatar} isFirstRow={index === 0} />
                    ))}
                </div>
            ];
        }

        const pagesArray: React.ReactNode[] = batches.map((batch, index) => (
            <div key={index} className="flex flex-1 flex-col w-full gap-2 items-start">
                {batch.map((entity) => (
                    <EntityRow
                        key={entity.guid}
                        entity={entity}
                        showAvatar={showAvatar}
                        onInvestigationClick={onInvestigationClick}
                    />
                ))}
            </div>
        ));
        return pagesArray;
    }, [isLoading, list, batches, showAvatar, onInvestigationClick]);

    return <LayoutCarousel title={title} pages={pages} contentClassName="flex flex-1 flex-col justify-center px-6 flex-shrink-0 h-full min-h-[150px]" />;
};

export default TopRiskEntityChart;
